import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import StagePage from "layouts/StagePage";
import GraphDiagram from "components/Diagrams/GraphDiagram";
import ArrayDiagram from "components/Diagrams/ArrayDiagram";
import StackDiagram from "components/Diagrams/StackDiagram";
import QueueDiagram from "components/Diagrams/QueueDiagram";
import HashTableDiagram from "components/Diagrams/HashTableDiagram";
import SetDiagram from "components/Diagrams/SetDiagram";
import ProgrammingPageView from "./ProgrammingPageView";
import axiosInstance from "apiHandles/axiosInstance";
import { useLanguage } from "context/LanguageProvider";

const programming = ["Python", "JavaScript", "C++", "Java", "C#"];

function StagePageView() {
  const { structureName, substructureId } = useParams();
  const navigate = useNavigate();

  const [allSubStructures, setAllSubStructures] = useState([]);
  const [substructureName, setSubstructureName] = useState("");
  const [validSubstructure, setValidSubstructure] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [finalStages, setFinalStages] = useState([]);
  const [dataReady, setDataReady] = useState(false);
  const [loadingStages, setLoadingStages] = useState(false);

  const structure = structureName;
  const { selLanguage, toggleLanguage, isLading } = useLanguage();

  const fetchSubStructures = async () => {
    const response = await axiosInstance.get("/api/substructure/");
    return response.data;
  };

  const fetchStagesForSubstructure = async (id) => {
    try {
      setLoadingStages(true);
      const cacheKey = `substructure_stages_${id}`;
      const cached = sessionStorage.getItem(cacheKey);
      if (cached) return JSON.parse(cached);
      const response = await axiosInstance.get(`/api/substructure/${id}/stages/`);
      sessionStorage.setItem(cacheKey, JSON.stringify(response.data));
      return response.data;
    } finally {
      setLoadingStages(false);
    }
  };

  useEffect(() => {
    const fetchData = async () => {
      setIsLoading(true);
      try {
        const idNum = Number(substructureId);
        if (!Number.isFinite(idNum)) {
          setValidSubstructure(null);
          setFinalStages([]);
          setDataReady(true);
          return;
        }

        const fetchedSubStructures = await fetchSubStructures();
        setAllSubStructures(fetchedSubStructures);

        const matched = fetchedSubStructures.find((sub) => sub.id === idNum);
        if (!matched) {
          setValidSubstructure(null);
          setSubstructureName("");
          setFinalStages([]);
          setDataReady(true);
          return;
        }

        setValidSubstructure(matched);
        setSubstructureName(matched.name);
        localStorage.setItem("currentSubstructureId", idNum);

        const stagesData = await fetchStagesForSubstructure(idNum);
        if (stagesData && Array.isArray(stagesData.stages)) {
          setFinalStages(stagesData.stages);
        } else {
          setFinalStages([]);
        }

        setDataReady(true);
      } catch (error) {
        if (error.response?.status === 401) {
          alert("Πρέπει να κάνεις login ξανά");
          localStorage.removeItem("access");
          navigate("/login");
        } else if (error.response?.status === 403) {
          alert(selLanguage ? "CSRF error. Δοκίμασε ξανά." : "CSRF error. Please try again.");
        } else {
          alert("Σφάλμα: " + error.message);
        }
      } finally {
        setIsLoading(false);
      }
    };

    fetchData();
  }, [navigate, substructureId]);

  const getResponsiveDiagramConfig = () => {
    return { radius: 120, centerX: 500, centerY: 300 };
  };

  const diagram = {
    Graph: {
      structureType: "Graph",
      finalStages,
      nodes: finalStages.map((stage, index) => {
        const config = getResponsiveDiagramConfig();
        const totalNodes = finalStages.length;
        const angle = (index * 2 * Math.PI) / totalNodes - Math.PI / 2;
        return {
          id: String(index + 1),
          position: {
            x: Math.cos(angle) * config.radius + config.centerX,
            y: Math.sin(angle) * config.radius + config.centerY,
          },
          data: { label: stage.name, stageId: stage.id, fullStageData: stage },
        };
      }),
      edges: [
        { id: "e1-2", source: "1", target: "2" },
        { id: "e2-3", source: "2", target: "3" },
        { id: "e4-1", source: "1", target: "4" },
        { id: "e3-1", source: "1", target: "3" },
      ],
    },
    Tree: {
      structureType: "Tree",
      finalStages,
      nodes: finalStages.map((stage, index) => {
        let level = 0, xPos = 150;
        if (index === 0) { level = 0; xPos = 120; }
        else if (index === 1) { level = 1; xPos = 20; }
        else if (index === 2) { level = 1; xPos = 250; }
        else if (index === 3) { level = 2; xPos = 0; }
        else if (index === 4) { level = 2; xPos = 250; }
        return {
          id: String(index + 1),
          position: { x: xPos, y: level * 100 },
          data: { label: stage.name, stageId: stage.id, fullStageData: stage },
        };
      }),
      edges: finalStages.slice(1).map((_, index) => {
        const target = String(index + 2);
        let source;
        if (index === 0 || index === 1) source = "1";
        else if (index === 2) source = "2";
        else if (index === 3) source = "3";
        return { id: `e${source}-${target}`, source, target };
      }),
    },
    List: {
      structureType: "List",
      finalStages,
      nodes: finalStages.map((stage, index) => ({
        id: String(index + 1),
        position: { x: index * 200, y: 0 },
        data: { label: stage.name, stageId: stage.id, fullStageData: stage },
        style: { width: 150 },
      })),
      edges: finalStages.slice(1).map((_, index) => ({
        id: `e${index + 1}-${index + 2}`,
        source: String(index + 1),
        target: String(index + 2),
      })),
    },
  };

  const isChaining = substructureName === "Chaining";
  const hashData = {
    table: isChaining
      ? [[], [finalStages[0]?.name || "Stage 1"], [], finalStages.slice(1, 3).map((s) => s.name), [], [finalStages[3]?.name || "Stage 4"]]
      : [[], [finalStages[0]?.name || "Stage 1"], [], finalStages.slice(1, 3).map((s) => s.name), []],
  };

  const messageStyles = {
    color: "white",
    fontSize: "1rem",
    fontFamily: "Poppins, sans-serif",
    textAlign: "center",
    padding: "1.5rem",
    background: "rgba(255, 255, 255, 0.05)",
    borderRadius: "0.75rem",
    border: "1px solid rgba(255, 255, 255, 0.1)",
    backdropFilter: "blur(10px)",
    margin: "1rem 0",
    maxWidth: "100%",
    wordWrap: "break-word",
  };

  const renderDiagram = () => {
    if (!dataReady) return <p style={messageStyles}>Preparing data...</p>;
    if (!validSubstructure) return <p style={messageStyles}>Δεν βρέθηκε η υποκατηγορία.</p>;
    if (loadingStages) return <p style={messageStyles}>Loading stages...</p>;
    if (finalStages.length === 0) return <p style={messageStyles}>Δεν υπάρχουν διαθέσιμα στάδια.</p>;

    const wrapperStyle = {
      width: "100%", maxWidth: "100%",
      display: "flex", justifyContent: "center", alignItems: "center",
      overflow: "hidden",
    };

    // ── Programming languages ─────────────────────────────────────────────
    if (programming.includes(structure)) {
      return (
        <div style={wrapperStyle}>
          <ProgrammingPageView
            languageName={structure}
            substructureName={substructureName}
            finalStages={finalStages}
            items={finalStages.map((s) => s.name)}
          />
        </div>
      );
    }

    // ── Data structures ───────────────────────────────────────────────────
    if (structure === "Array")
      return <div style={wrapperStyle}><ArrayDiagram items={finalStages.map((s) => s.name)} finalStages={finalStages} /></div>;

    if (structure === "Stack")
      return <div style={wrapperStyle}><StackDiagram items={finalStages.map((s) => s.name)} finalStages={finalStages} /></div>;

    if (structure === "Queue")
      return <div style={wrapperStyle}><QueueDiagram items={finalStages.map((s) => s.name)} finalStages={finalStages} /></div>;

    if (structure === "HashTable")
      return <div style={wrapperStyle}><HashTableDiagram table={hashData.table} finalStages={finalStages} /></div>;

    if (structure === "Set")
      return <div style={wrapperStyle}><SetDiagram items={finalStages.map((s) => s.name)} finalStages={finalStages} /></div>;

    if (structure === "List")
      return (
        <div style={wrapperStyle}>
          <GraphDiagram nodes={diagram.List.nodes} edges={diagram.List.edges} structureType="List" finalStages={finalStages} />
        </div>
      );

    if (diagram[structure])
      return (
        <div style={wrapperStyle}>
          <GraphDiagram
            nodes={diagram[structure].nodes}
            edges={diagram[structure].edges}
            structureType={diagram[structure].structureType}
            finalStages={finalStages}
          />
        </div>
      );

    return <p style={messageStyles}>Δεν υπάρχει διαθέσιμο διάγραμμα για αυτή τη δομή.</p>;
  };

  return (
    <StagePage bigger_title={`${structureName}`} title={substructureName || ""}>
      {isLoading ? <p style={messageStyles}>Loading...</p> : renderDiagram()}
    </StagePage>
  );
}

export default StagePageView;